import { useEffect } from "react";
import { Skeleton } from "@/v2/components/ui/skeleton";
import { usePlayersByTeams } from "../api";
import { TeamPlayerGroup } from "./TeamPlayerGroup";

type PlayersColumnProps = {
  selectedTeam: (string | number)[];
  selectedPlayer: (string | number)[];
  setSelectedPlayer: (players: (string | number)[]) => void;
  selectAllTeamPlayers: (string | number)[];
  setSelectAllTeamPlayers: (all: (string | number)[]) => void;
};

export function PlayersColumn({
  selectedTeam,
  selectedPlayer,
  setSelectedPlayer,
  selectAllTeamPlayers,
  setSelectAllTeamPlayers,
}: PlayersColumnProps) {
  const { data: teamGroups, isLoading } = usePlayersByTeams(selectedTeam);

  // Ported from v1 ChannelCards' early-return branch: no team selected means
  // no individually-selected players either.
  useEffect(() => {
    if (selectedTeam.length === 0) {
      setSelectedPlayer([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam.length]);

  return (
    <div className="rounded-xl border border-slate-200 bg-card flex flex-col min-h-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="font-bold text-slate-900 text-base">3 · Players</div>
        <span className="text-sm font-bold px-3 py-1 rounded-full bg-action-light text-action">
          {selectedPlayer.length} selected{selectAllTeamPlayers.length > 0 ? ` + ${selectAllTeamPlayers.length} team(s) all` : ""}
        </span>
      </div>
      <div className="p-3 overflow-y-auto max-h-80">
        <div className="rounded-lg border border-green-200 bg-green-50 text-green-800 text-sm px-3 py-2 mb-3">
          Optional. Skip this to follow all players on the selected teams.
        </div>
        {selectedTeam.length === 0 ? (
          <div className="text-base text-slate-500 text-center py-6">Please Select Team</div>
        ) : isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          (teamGroups ?? []).map((team) => (
            <TeamPlayerGroup
              key={team.team_id}
              team={team}
              selectedPlayer={selectedPlayer}
              setSelectedPlayer={setSelectedPlayer}
              selectAllTeamPlayers={selectAllTeamPlayers}
              setSelectAllTeamPlayers={setSelectAllTeamPlayers}
            />
          ))
        )}
      </div>
    </div>
  );
}
